/**
 * Main HTTP request hook
 * Provides React hook interface for HTTP operations
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { apiRequest } from '../utils/api-client'
import type { UseHttpOptions, UseHttpReturn, HttpError } from '../types'

/**
 * React hook for HTTP requests
 * @param url - Request URL
 * @param options - Request options
 * @returns Hook return object with data, loading state, and control functions
 */
export function useHttp<T = any>(
  url: string,
  options: UseHttpOptions = {}
): UseHttpReturn<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<HttpError | null>(null)
  
  // Use ref to store the abort controller
  const abortControllerRef = useRef<AbortController | null>(null)
  const executedRef = useRef(false)
  
  /**
   * Cancels the current request
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setLoading(false)
  }, [])
  
  /**
   * Executes the HTTP request
   */
  const execute = useCallback(async (executeOptions?: Partial<UseHttpOptions>): Promise<T> => {
    // Cancel any existing request
    cancel()
    
    // Create new abort controller
    abortControllerRef.current = new AbortController()
    
    // Merge options
    const mergedOptions = {
      ...options,
      ...executeOptions,
      abortSignal: abortControllerRef.current.signal,
    }
    
    setLoading(true)
    setError(null)
    
    try {
      const result = await apiRequest(url, mergedOptions)
      
      // Only update state if request wasn't cancelled
      if (!abortControllerRef.current?.signal.aborted) {
        setData(result)
        setError(null)
        
        // Call success callback if provided
        if (mergedOptions.onSuccess) {
          mergedOptions.onSuccess(result)
        }
      }
      
      return result
    } catch (err) {
      const httpError = err as HttpError
      
      // Only update state if request wasn't cancelled
      if (!abortControllerRef.current?.signal.aborted) {
        setError(httpError)
        setData(null)
        
        // Call error callback if provided
        if (mergedOptions.onError) {
          mergedOptions.onError(httpError)
        }
      }
      
      throw httpError
    } finally {
      // Only update loading state if request wasn't cancelled
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false)
      }
      abortControllerRef.current = null
    }
  }, [url, options, cancel])
  
  /**
   * Retries the last request
   */
  const retry = useCallback(async (): Promise<T> => {
    return execute()
  }, [execute])
  
  // Execute immediately if immediate option is true
  useEffect(() => {
    if (options.immediate && !executedRef.current) {
      executedRef.current = true
      execute().catch(() => {
        // Error is already handled in execute function
      })
    }
  }, [options.immediate, execute])
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel()
    }
  }, [cancel])
  
  return {
    data,
    loading,
    error,
    execute,
    cancel,
    retry,
  }
}

/**
 * Hook for GET requests with immediate execution
 */
export function useGet<T = any>(
  url: string,
  options: Omit<UseHttpOptions, 'method'> = {}
): UseHttpReturn<T> {
  return useHttp<T>(url, {
    ...options,
    method: 'GET',
    immediate: true,
  })
}

/**
 * Hook for POST requests
 */
export function usePost<T = any>(
  url: string,
  options: Omit<UseHttpOptions, 'method'> = {}
): UseHttpReturn<T> {
  return useHttp<T>(url, {
    ...options,
    method: 'POST',
  })
}

/**
 * Hook for PUT requests
 */
export function usePut<T = any>(
  url: string,
  options: Omit<UseHttpOptions, 'method'> = {}
): UseHttpReturn<T> {
  return useHttp<T>(url, {
    ...options,
    method: 'PUT',
  })
}

/**
 * Hook for DELETE requests
 */
export function useDelete<T = any>(
  url: string,
  options: Omit<UseHttpOptions, 'method'> = {}
): UseHttpReturn<T> {
  return useHttp<T>(url, {
    ...options,
    method: 'DELETE',
  })
}